import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function PushNotifications({ variant = "card" }) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vapidKey, setVapidKey] = useState("");

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    (async () => {
      try {
        const [r, reg] = await Promise.all([
          api.get("/push/vapid-public-key"),
          navigator.serviceWorker.ready,
        ]);
        setVapidKey(r.data.public_key || "");
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (err) { console.warn("push init", err); }
    })();
  }, []);

  const enable = async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        if (!vapidKey) { toast.error("Configurazione notifiche non disponibile"); return; }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      // Test push
      try { await api.post("/push/test"); } catch {}
      toast.success("Notifiche attive: ti avviseremo per scadenze e nuovi allegati");
    } catch (err) {
      console.error("push enable", err);
      toast.error("Impossibile attivare le notifiche");
    } finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await api.delete("/push/subscribe", {
            data: { endpoint: sub.endpoint, keys: sub.toJSON().keys },
          });
        } catch (err) { console.warn("push server delete", err); }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (err) {
      console.error("push disable", err);
      toast.error("Errore");
    } finally { setLoading(false); }
  };

  if (!supported) {
    return variant === "card" ? (
      <div className="bg-white border border-black/10 rounded-xl p-4 text-xs text-[#8A8A8A]" data-testid="push-unsupported">
        Le notifiche push non sono supportate da questo browser.
      </div>
    ) : null;
  }

  const blocked = permission === "denied";

  if (variant === "compact") {
    return (
      <button
        data-testid={subscribed ? "push-toggle-off" : "push-toggle-on"}
        onClick={subscribed ? disable : enable}
        disabled={loading || blocked}
        className={`inline-flex items-center gap-2 rounded-xl h-9 px-3 text-xs font-medium transition-colors ${
          subscribed
            ? "bg-[#009966]/10 text-[#009966] border border-[#009966]/30"
            : "border border-black/15 hover:bg-black/5 text-[#0A0A0A]"
        }`}
      >
        {subscribed ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
        {subscribed ? "Notifiche attive" : "Attiva notifiche"}
      </button>
    );
  }

  return (
    <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="push-card">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          subscribed ? "bg-[#009966]/10 text-[#009966]" : "bg-[#0A0A0A]/5 text-[#0A0A0A]"
        }`}>
          {subscribed ? <BellRing className="w-5 h-5" /> : blocked ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#8A8A8A] mb-1">NOTIFICHE PUSH</div>
          <div className="font-display font-bold text-lg tracking-tight">
            {subscribed ? "Notifiche attive" : blocked ? "Notifiche bloccate" : "Attiva le notifiche"}
          </div>
          <div className="text-xs text-[#5C5C5C] mt-1 leading-relaxed">
            {blocked
              ? "Hai bloccato le notifiche dal browser. Riabilitale dalle impostazioni del sito."
              : subscribed
                ? "Ti avviseremo per le scadenze fiscali (730/CU/ISEE/IMU) e ogni volta che l'import email trova nuovi allegati."
                : "Ricevi un promemoria 7, 3 e 1 giorno prima di ogni scadenza fiscale e quando l'import email trova nuovi documenti."}
          </div>
        </div>
        <button
          data-testid={subscribed ? "push-disable-btn" : "push-enable-btn"}
          onClick={subscribed ? disable : enable}
          disabled={loading || blocked}
          className={`shrink-0 rounded-xl h-10 px-4 text-sm font-medium transition-colors ${
            subscribed
              ? "border border-black/15 hover:bg-black/5 text-[#0A0A0A]"
              : "bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white disabled:opacity-50"
          }`}
        >
          {loading ? "…" : subscribed ? "Disattiva" : "Attiva"}
        </button>
      </div>
    </div>
  );
}
